import {
  CARRIER_RESOLUTION,
  ORDER_CHANNELS,
  PENNYLANE_ORIGIN,
  channelLabel,
  countryCodeSchema,
  type CarrierSimulationResult,
} from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useId, type JSX } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { PageSection } from '@/components/layout/page.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { type ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { simulateCarrier } from './api.js';
import { useShippingMethods } from './use-shipping-methods.js';

/**
 * Simulateur (`02` §9) : origine + mode de livraison + pays + code postal +
 * canal → règle retenue et raisonnement pas à pas.
 *
 * `NO_SHIPPING_METHOD` est la sentinelle du Select « Aucun » : elle envoie
 * `shippingMethod: null`, c'est-à-dire une commande **sans ligne de
 * livraison** — cas que le moteur traite à part (`no_shipping_line`), et qui
 * n'est pas la même chose qu'un mode qui ne correspond à aucune règle.
 */
const NO_SHIPPING_METHOD = '__aucun__';

const simulatorFormSchema = z.object({
  origin: z.string().min(1, 'Origine requise.'),
  shippingMethod: z.string(),
  country: countryCodeSchema,
  zip: z.string(),
  channel: z.string().min(1),
});

type SimulatorFormValues = z.infer<typeof simulatorFormSchema>;

export interface CarrierSimulatorProps {
  /** Boutique dont les modes de livraison alimentent les suggestions. */
  suggestionsStore?: string | undefined;
}

export function CarrierSimulator({ suggestionsStore }: CarrierSimulatorProps): JSX.Element {
  const stores = useStoresList();
  const shippingMethods = useShippingMethods(suggestionsStore);
  const methodListId = useId();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SimulatorFormValues>({
    resolver: zodResolver(simulatorFormSchema),
    defaultValues: { origin: '', shippingMethod: '', country: '', zip: '', channel: 'web' },
  });

  const mutation = useMutation<CarrierSimulationResult, ApiError, SimulatorFormValues>({
    mutationFn: (values) =>
      simulateCarrier({
        origin: values.origin,
        shippingMethod: values.shippingMethod === NO_SHIPPING_METHOD ? null : values.shippingMethod,
        country: values.country,
        ...(values.zip === '' ? {} : { zip: values.zip }),
        channel: values.channel,
      }),
    onError: (error) => {
      toast.fromError(error);
    },
  });

  const originOptions = [
    ...(stores.data?.items ?? []).map((store) => ({ code: store.code, label: store.name })),
    { code: PENNYLANE_ORIGIN, label: 'Pennylane' },
  ];

  return (
    <PageSection
      title="Simulateur"
      description="Rejoue la table de règles sur une commande fictive et montre le raisonnement, règle par règle."
    >
      <div className="flex flex-col gap-5">
        <form
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
          noValidate
          onSubmit={(event) => {
            void handleSubmit((values) => {
              mutation.mutate(values);
            })(event);
          }}
        >
          <Field label="Origine" htmlFor="sim-origin" error={errors.origin?.message}>
            <Controller
              control={control}
              name="origin"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(next) => {
                    field.onChange(next);
                  }}
                >
                  <SelectTrigger id="sim-origin" aria-invalid={errors.origin ? true : undefined}>
                    <SelectValue placeholder="Choisir…" />
                  </SelectTrigger>
                  <SelectContent>
                    {originOptions.map((option) => (
                      <SelectItem key={option.code} value={option.code}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>

          <Field label="Mode de livraison" htmlFor="sim-method">
            <Input
              id="sim-method"
              list={methodListId}
              placeholder="Livraison Standard"
              {...register('shippingMethod')}
            />
            <datalist id={methodListId}>
              <option value={NO_SHIPPING_METHOD} label="Aucun (commande sans expédition)" />
              {shippingMethods.map((method) => (
                <option key={method} value={method} />
              ))}
            </datalist>
          </Field>

          <Field label="Pays (ISO-2)" htmlFor="sim-country" error={errors.country?.message}>
            <Input
              id="sim-country"
              placeholder="FR"
              maxLength={2}
              aria-invalid={errors.country ? true : undefined}
              {...register('country', { setValueAs: (value: string) => value.toUpperCase() })}
            />
          </Field>

          <Field label="Code postal" htmlFor="sim-zip">
            <Input id="sim-zip" placeholder="20000" {...register('zip')} />
          </Field>

          <Field label="Canal" htmlFor="sim-channel">
            <Controller
              control={control}
              name="channel"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(next) => {
                    field.onChange(next);
                  }}
                >
                  <SelectTrigger id="sim-channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_CHANNELS.map((code) => (
                      <SelectItem key={code} value={code}>
                        {channelLabel(code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>

          <div className="sm:col-span-2 lg:col-span-5">
            <Button type="submit" isLoading={mutation.isPending}>
              Simuler
            </Button>
          </div>
        </form>

        {mutation.data ? (
          <div className="flex flex-col gap-3 border-t border-border pt-5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge registry={CARRIER_RESOLUTION} code={mutation.data.resolution} />
              <Badge tone="gray">{mutation.data.code}</Badge>
              <span className="font-medium">{mutation.data.label}</span>
              {mutation.data.pickup ? <Badge tone="blue">Point relais</Badge> : null}
            </div>
            {mutation.data.ruleName === null ? null : (
              <p>
                Règle retenue : <span className="font-medium">{mutation.data.ruleName}</span>
              </p>
            )}
            <p className="text-muted-foreground">
              Pays destinataire retenu : {mutation.data.countryCodeApplied}
            </p>
            <ol className="flex list-decimal flex-col gap-1 pl-5 text-muted-foreground">
              {/* Les étapes sont ordonnées et peuvent se répéter : la position est la seule clé stable. */}
              {mutation.data.steps.map((step, index) => (
                <li key={`${String(index)}-${step}`}>{step}</li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>
    </PageSection>
  );
}
